import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, InputNumber, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";
/**
 * Component to display the forms for updating the polling
 */
export default function PowerPickPolling() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  //useQuery for getting the ItemPath polling
  const { data: powerPickPoll } = useQuery({
    queryKey: ["powerPickPoll"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ name: "powerPickPoll" }))
        .then((response) => {
          return response.settings[0];
        });
    },
    initialData: { value: undefined },
    select: (data: SettingType) => {
      return data;
    },
  });
  //useMutation for updating the ItemPath polling
  const { mutateAsync: updatePolling } = useMutation({
    mutationFn: (updatePolling: SettingType) => {
      return settingService
        .updateSingle(updatePolling.id, { value: updatePolling.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["powerPickPoll"], data.setting);
    },
  });
  //useMutation for creating the ItemPath polling
  const { mutateAsync: createPolling } = useMutation({
    mutationFn: (createPolling: SettingType) => {
      return settingService
        .createSingle({
          value: createPolling.value,
          name: "powerPickPoll",
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["powerPickPoll"], data.setting);
    },
  });
  //onSubmit handler for creating/updating the ItemPath polling
  const onSubmitPolling = useCallback(
    async (
      {
        value,
      }: {
        value: string;
      },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      if (powerPickPoll?.id) {
        await updatePolling({ id: powerPickPoll.id, value }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      } else {
        await createPolling({ value }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      }
    },
    [createPolling, updatePolling, powerPickPoll.id],
  );
  /**
   * Update polling form
   */
  const updatePollingForm: (
    props: FormikProps<{ value: string | undefined }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{`${t("translation:power_pick_polling")}`}</h3>
            <p>{t("translation:power_pick_polling_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item
              className="no-label" // skipcq: JS-0394
              name="value"
            >
              <InputNumber name="value" size="large" min={1} max={30} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );
  return (
    <Formik
      component={updatePollingForm}
      enableReinitialize
      initialValues={{
        value: powerPickPoll?.value ?? 5,
      }}
      onSubmit={onSubmitPolling}
    />
  );
}
